import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { FortisPayment, type FortisPaymentHandle } from '../components/payments/FortisPayment'
import * as bandsApi from '../api/bands'
import { PaymentPending } from '../components/payments/PaymentPending'
import { PAYMENT_PENDING_TEXT, isDefiniteRefusal, pendingRefusal, serverError } from '../api/paymentOutcome'
import { stockYearOptions, stockYearFallback, type StockYearOption } from './stockBandYears'
import { stockShippingCost, stockOrderTotal } from './stockBandShipping'
import type { FortisTransactionData, StockShippingSchedule } from '../types'

export function StockBands() {
  const { user } = useAuth()
  const fortisRef = useRef<FortisPaymentHandle>(null)

  const [quantity, setQuantity] = useState(5)
  // Which year's bands. The server says which years are on offer (currently the
  // next-season pre-order only); the member must still pick one explicitly.
  const [yearOptions, setYearOptions] = useState<StockYearOption[]>([])
  const [yearsFailed, setYearsFailed] = useState(false)
  const [bandYear, setBandYear] = useState<number | null>(null)
  // Shipping is tiered by quantity (Lauren, 2026-09-16, simplified 2026-09-17):
  // $8 for 100 bands or fewer, the personalized postage tier above that, and one
  // mailing option. The server sends the schedule with the settings; until it
  // arrives the form shows no price.
  const [shippingSchedule, setShippingSchedule] = useState<StockShippingSchedule | null>(null)
  const [step, setStep] = useState<'form' | 'payment' | 'pending' | 'success'>('form')
  const [pendingMessage, setPendingMessage] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [paymentData, setPaymentData] = useState<{
    clientToken: string
    env: 'sandbox' | 'production'
    transactionId: string
    costData: { total: number; items: { label: string; amount: number }[] }
  } | null>(null)
  const [shipping, setShipping] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: user?.phone || '',
  })

  useEffect(() => {
    let cancelled = false
    bandsApi
      .getBandSettings()
      .then((s) => {
        if (cancelled) return
        const options = stockYearOptions(s)
        setYearOptions(options)
        setYearsFailed(options.length === 0)
        setShippingSchedule(s.stockShipping ?? null)
        if (s.pendingCapture) {
          setPendingMessage(s.pendingMessage || PAYMENT_PENDING_TEXT)
          setStep('pending')
        }
      })
      .catch(() => {
        if (cancelled) return
        setYearOptions(stockYearOptions({ stockYears: stockYearFallback() }))
        setYearsFailed(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Update shipping when user loads
  useEffect(() => {
    if (user) {
      setShipping((s) => ({
        ...s,
        firstName: s.firstName || user.firstName || '',
        lastName: s.lastName || user.lastName || '',
        phone: s.phone || user.phone || '',
      }))
    }
  }, [user])

  const handleStartPayment = useCallback(async () => {
    setError('')

    if (quantity <= 0 || quantity % 5 !== 0) {
      setError('Quantity must be a multiple of 5 (minimum 5)')
      return
    }

    if (bandYear === null) {
      setError('Please choose which year of stock bands you want')
      return
    }

    const requiredFields = ['firstName', 'lastName', 'address', 'city', 'state', 'zip', 'phone'] as const
    const missing = requiredFields.filter((f) => !shipping[f])
    if (missing.length > 0) {
      setError('Please fill in all shipping fields')
      return
    }

    setSubmitting(true)

    try {
      const result = await bandsApi.startStockBandOrder({
        quantity,
        bandYear,
        shippingDetails: shipping,
      })
      setPaymentData({
        clientToken: result.clientToken,
        env: result.env,
        transactionId: result.transactionId,
        costData: result.costData,
      })
      setStep('payment')
    } catch (err) {
      // An earlier band order still waits on its capture: no payment form.
      const held = pendingRefusal(err)
      if (held) {
        setPendingMessage(held)
        setStep('pending')
        return
      }
      // Surface the server's reason (e.g. an outdated page missing bandYear)
      // instead of a generic message.
      const serverMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setError(serverMessage ? `${serverMessage}. If this keeps happening, reload the page and try again.` : 'Failed to start payment. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }, [quantity, bandYear, shipping])

  // Belt and braces: FortisPayment now ignores 'done' from unmounted elements,
  // and the server serializes completions per Fortis transaction. Still never
  // complete twice from this page.
  const completedRef = useRef(false)

  const handlePaymentSuccess = useCallback(async (fortisData: FortisTransactionData) => {
    if (!paymentData) return
    if (completedRef.current) return
    completedRef.current = true
    setSubmitting(true)
    setError('')

    try {
      const result = await bandsApi.completeStockBandOrder(paymentData.transactionId, {
        id: fortisData.id,
        status_code: fortisData.status_code,
      })
      if (result?.pending) {
        // completedRef stays set and the payment form is gone: the member must not pay again.
        setPendingMessage(result.message || PAYMENT_PENDING_TEXT)
        setPaymentData(null)
        setStep('pending')
        return
      }
      setStep('success')
    } catch (err) {
      completedRef.current = false
      if (isDefiniteRefusal(err)) {
        // The server refused this order for good: Proceed to Payment starts a new one.
        setError(serverError(err) ?? 'There was an error completing your order. Please contact support.')
        setPaymentData(null)
        setStep('form')
        return
      }
      setError('Payment was processed but there was an error completing your order. Please contact support.')
    } finally {
      setSubmitting(false)
    }
  }, [paymentData])

  const handlePaymentError = useCallback((msg: string) => {
    setError(msg)
  }, [])

  if (step === 'pending') {
    return <PaymentPending message={pendingMessage} />
  }

  if (step === 'success') {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <div className="text-success text-5xl mb-4">&#10003;</div>
        <h1 className="text-2xl font-heading font-bold text-text mb-2">Stock Band Order Placed!</h1>
        <p className="text-text-muted mb-6">
          Your order for {quantity} {bandYear} stock bands has been submitted successfully.
        </p>
        <a href="/dashboard" className="inline-block px-6 py-2.5 bg-primary text-text-inverse rounded-md hover:bg-primary-light transition-colors">
          Back to Dashboard
        </a>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-heading font-bold text-text mb-2">
        Order Stock Bands
      </h1>
      <p className="text-text-muted mb-6">
        Stock bands are available year-round. No personalized lettering or color selection.
      </p>

      {error && (
        <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {step === 'form' && (
        <div className="border border-border rounded-lg p-6 bg-surface">
          <div className="mb-6">
            <label className="block text-sm font-medium text-text mb-1">Quantity</label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
              min={5}
              step={5}
              className="w-full max-w-xs px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
            <p className="text-xs text-text-muted mt-1">
              Minimum 5 bands, sold in increments of 5. $0.45 per band.
            </p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-text mb-2">Band year</label>
            <div className="space-y-2">
              {yearOptions.map((option) => (
                <label key={option.year} className="flex items-center gap-2 text-sm text-text cursor-pointer">
                  <input
                    type="radio"
                    name="bandYear"
                    checked={bandYear === option.year}
                    onChange={() => setBandYear(option.year)}
                  />
                  {option.label}
                </label>
              ))}
              {yearOptions.length === 0 && (
                <p className="text-xs text-text-muted">
                  {yearsFailed
                    ? 'Band years could not be loaded. Please reload the page.'
                    : 'Loading available years...'}
                </p>
              )}
            </div>
            <p className="text-xs text-text-muted mt-2">
              Choose the year printed on the bands you need.
            </p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-text mb-2">Shipping</label>
            <p className="text-sm text-text">
              {shippingSchedule
                ? `$${stockShippingCost(shippingSchedule, quantity).toFixed(2)}`
                : 'Shown at payment.'}
            </p>
            <p className="text-xs text-text-muted mt-2">
              Shipping is $8.00 for orders of 100 bands or fewer. Larger orders ship at the same
              rates as personalized bands.
            </p>
            <p className="text-xs text-text-muted mt-2">
              {shippingSchedule
                ? `Order total: $${stockOrderTotal(shippingSchedule, quantity).toFixed(2)}`
                : 'Shipping and the order total are shown at payment.'}
            </p>
          </div>

          <h3 className="font-heading font-semibold text-text mb-3">Shipping Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="First Name" value={shipping.firstName} onChange={(v) => setShipping((s) => ({ ...s, firstName: v }))} />
            <FormField label="Last Name" value={shipping.lastName} onChange={(v) => setShipping((s) => ({ ...s, lastName: v }))} />
            <div className="sm:col-span-2">
              <FormField label="Address" value={shipping.address} onChange={(v) => setShipping((s) => ({ ...s, address: v }))} />
            </div>
            <FormField label="City" value={shipping.city} onChange={(v) => setShipping((s) => ({ ...s, city: v }))} />
            <FormField label="State" value={shipping.state} onChange={(v) => setShipping((s) => ({ ...s, state: v }))} />
            <FormField label="ZIP Code" value={shipping.zip} onChange={(v) => setShipping((s) => ({ ...s, zip: v }))} />
            <FormField label="Phone" value={shipping.phone} onChange={(v) => setShipping((s) => ({ ...s, phone: v }))} />
          </div>

          <button
            type="button"
            onClick={handleStartPayment}
            disabled={submitting}
            className="mt-6 px-6 py-2.5 bg-primary text-text-inverse rounded-md hover:bg-primary-light transition-colors disabled:opacity-50"
          >
            {submitting ? 'Processing...' : 'Proceed to Payment'}
          </button>
        </div>
      )}

      {step === 'payment' && paymentData && (
        <div className="border border-border rounded-lg p-6 bg-surface">
          <h2 className="font-heading font-semibold text-text mb-4">Payment</h2>

          <div className="mb-4 p-3 bg-surface-alt rounded-md text-sm">
            {paymentData.costData.items.map((item, i) => (
              <div key={i} className="flex justify-between text-text">
                <span>{item.label}</span>
                <span>${item.amount.toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between font-semibold text-text border-t border-border mt-2 pt-2">
              <span>Total</span>
              <span>${paymentData.costData.total.toFixed(2)}</span>
            </div>
          </div>

          <FortisPayment
            ref={fortisRef}
            clientToken={paymentData.clientToken}
            environment={paymentData.env}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
            showSubmitButton={true}
          />

          {submitting && (
            <div className="flex items-center mt-4 text-text-muted text-sm">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent mr-2" />
              Completing your order...
            </div>
          )}

          <button
            type="button"
            onClick={() => { setStep('form'); setPaymentData(null) }}
            className="mt-4 px-4 py-2 border border-border rounded-md text-text hover:bg-surface-hover transition-colors"
            disabled={submitting}
          >
            Back
          </button>
        </div>
      )}
    </div>
  )
}

function FormField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-text mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
      />
    </div>
  )
}
